import { useState } from 'react'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import { PHONE_REGEX } from 'src/utils/regex'
import addressRequest from 'src/api/requests/address'
import { PATH as AUTOMATIC_PLAN_PATH } from 'src/pages/automaticPlan'
import { useAppDispatch, useAppSelector } from 'src/store'
import { register } from 'src/useCase/user'
import appActions from 'src/store/ui/app'
import backFlowActions from 'src/store/domain/automaticPlan/backFlow'

export type SMSInputFormValues = {
  phone: string
}

export const useSMSInputForm = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const userInfo = useAppSelector((state) => state.ui.app.authInfo)
  const menuId = searchParams.get('menu_id') || searchParams.get('menuId') || ''
  const registrationData = location.state?.registrationData
  // [SCOPE] No.154 — the Contact API used for the CS notification requires a
  // name/email; by the time a user reaches phone verification they already
  // registered (userInfo/registrationData holds it), so reuse it here instead
  // of asking again.
  const duplicateContactName = userInfo
    ? `${userInfo.family_name || ''} ${userInfo.given_name || ''}`.trim()
    : `${registrationData?.family_name || ''} ${
        registrationData?.given_name || ''
      }`.trim()
  const duplicateContactEmail = userInfo?.email || registrationData?.email || ''
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)
  const [hasPhoneDuplicateError, setHasPhoneDuplicateError] = useState(false)
  const [duplicatePhone, setDuplicatePhone] = useState('')

  const defaultValues: SMSInputFormValues = {
    phone: '',
  }

  const validationSchema = Yup.object().shape({
    phone: Yup.string()
      .required('入力してください。')
      .matches(
        PHONE_REGEX,
        '電話番号は０から始まる10～11桁で入力してください。',
      ),
  })

  // mode:'all' validates on every change and blur, so formState.isValid
  // reflects the current input value from the first keystroke — this is
  // required for the disabled={!formState.isValid} button guard to work
  // correctly from the initial render.
  const form = useForm<SMSInputFormValues>({
    defaultValues,
    shouldFocusError: true,
    mode: 'all',
    resolver: yupResolver(validationSchema),
  })

  const handleBlur = async (value: string) => {
    if (!value) return

    const { phone: phoneError } = form.formState.errors
    if (phoneError) return

    try {
      await addressRequest.verifyUser({ phone: value })
      form.clearErrors('phone')
      setHasPhoneDuplicateError(false)
    } catch (err: any) {
      const apiMessage =
        err?.response?.data?.errors?.phone?.[0] || err?.response?.data?.message

      // [SCOPE] Fallback to Japanese string check during the deploy transition
      // window before api#1033 is live.
      const isDuplicateError =
        err?.response?.data?.error_code === 'DUPLICATE_PHONE' ||
        apiMessage?.includes('既に使われています')

      if (isDuplicateError) {
        setDuplicatePhone(value)
        setIsDuplicateModalOpen(true)
        setHasPhoneDuplicateError(true)
      }

      form.setError('phone', {
        type: 'manual',
        message: apiMessage,
      })
    }
  }

  const handlePhoneChange = () => {
    setHasPhoneDuplicateError(false)
  }

  const onSubmit = async (data: SMSInputFormValues) => {
    try {
      dispatch(
        backFlowActions.setBackFlow({
          from: '/phone-verification',
          params: Object.fromEntries(searchParams.entries()),
        }),
      )

      if (registrationData) {
        const combinedData = { ...registrationData, phone: data.phone }
        await dispatch(
          register({
            credentials: combinedData,
            meta: form as any,
            redirectPath: `${AUTOMATIC_PLAN_PATH.SMS_AUTH}?menu_id=${
              menuId || ''
            }&${searchParams.toString()}`,
          }),
        ).unwrap()
      } else {
        const res = await addressRequest.updatePhone({ phone: data.phone })
        if (res.data) {
          // update phone in user
          dispatch(
            appActions.patchAuthInfo({
              phone: res.data.phone,
              otp_expire_at: res.data.otp_expire_at,
              otp_verified_at: res.data.otp_verified_at,
            }),
          )
          const params = new URLSearchParams(searchParams.toString())
          if (menuId) params.set('menu_id', menuId)

          navigate(`${AUTOMATIC_PLAN_PATH.SMS_AUTH}?${params.toString()}`)
        }
      }
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.errors?.phone?.[0] ||
        error?.response?.data?.message

      if (apiMessage) {
        form.setError('phone', {
          type: 'manual',
          message: apiMessage,
        })
      }
    }
  }

  return {
    form,
    onSubmit,
    handleBlur,
    handlePhoneChange,
    hasPhoneDuplicateError,
    isDuplicateModalOpen,
    setIsDuplicateModalOpen,
    duplicatePhone,
    duplicateContactName,
    duplicateContactEmail,
  }
}
